import { useState } from 'react';
import { Check, ChevronDown, Languages } from 'lucide-react';
import { LANGUAGES, t, useLanguageStore, type Language } from '../../i18n';
import { authApi } from '../../services/api';
import { useAuthStore } from '../../store';
import { cn } from '../../utils/helpers';

/** True once someone picks a language on the sign-in page; that choice then wins over the one saved in the account. */
let chosenWhileSignedOut = false;
export const languageChosenWhileSignedOut = () => chosenWhileSignedOut;

/** Choose the language: saved in this browser and, when signed in, with the account (also used for e-mails). */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { language, setLanguage } = useLanguageStore();
  const [open, setOpen] = useState(false);

  const choose = (code: Language) => {
    setOpen(false);
    if (code === language) return;
    const { user, setUser } = useAuthStore.getState();
    if (!user) {
      chosenWhileSignedOut = true;
      setLanguage(code);
      return;
    }
    // Saved first, so the account is never read back with the old language
    authApi
      .setLanguage(code)
      .then(setUser)
      .catch(() => undefined)
      .finally(() => setLanguage(code));
  };

  return (
    <div className={cn('relative', className)}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-100"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('Jazyk')}
        title={t('Jazyk')}
      >
        <Languages className="h-4 w-4 text-secondary-500" aria-hidden="true" />
        <span className="uppercase">{language}</span>
        <ChevronDown className="h-4 w-4 text-secondary-400" aria-hidden="true" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div role="menu" className="absolute right-0 z-50 mt-2 w-44 rounded-lg border border-secondary-200 bg-white py-1 shadow-lg">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                role="menuitemradio"
                aria-checked={l.code === language}
                lang={l.code}
                onClick={() => choose(l.code)}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-secondary-50',
                  l.code === language ? 'font-medium text-primary-700' : 'text-secondary-700',
                )}
              >
                <span className="w-6 text-xs font-semibold uppercase text-secondary-400">{l.code}</span>
                <span className="flex-1">{l.label}</span>
                {l.code === language && <Check className="h-4 w-4" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
